import React from 'react';
import {
  Box,
  Button,
  Card,
  Icon,
  Spinner,
  Text,
  Title,
} from '@nimbus-ds/components';
import { BarcodeIcon, CalculatorIcon, CreditCardIcon } from '@nimbus-ds/icons';
import { ErrorMessage } from 'commons/components';
import { useIsMobile } from 'commons/hooks';
import { PaymentMethodsType } from 'commons/types';
import { useTranslationWithPrefix } from 'commons/utils';
import { useGetPlanFees } from '../../hooks';

const DEFAULT_PLAN_EXTERNAL_ID = 'mex-tiendanube-plan';

interface FeeCalculatorSectionProps {
  showPrimaryAction?: boolean;
  primaryActionLabel?: string;
  onPrimaryAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  showSecondaryAction?: boolean;
}

const FeeCalculatorSection: React.FC<FeeCalculatorSectionProps> = ({
  showPrimaryAction = true,
  primaryActionLabel,
  onPrimaryAction,
  secondaryActionLabel,
  onSecondaryAction,
  showSecondaryAction = true,
}) => {
  const { t } = useTranslationWithPrefix('landing.feeCalculatorSection');

  const isMobile = useIsMobile();

  const {
    data: planFees = [],
    isLoading,
    error,
  } = useGetPlanFees(DEFAULT_PLAN_EXTERNAL_ID);

  const getPlan = (paymentMethod: PaymentMethodsType) =>
    planFees.find((fee) => fee.paymentMethod === paymentMethod);

  const planCards = [
    {
      key: 'card',
      titleKey: 'cards.card.title',
      receiveLabelKey: 'cards.card.receiveLabel',
      receiveValueKey: 'cards.card.receiveValue',
      feeLabelKey: 'cards.card.feeLabel',
      feeValueKey: 'cards.card.feeValue',
      icon: <CreditCardIcon size="large" />,
      plan: getPlan(PaymentMethodsType.CreditCard),
    },
    {
      key: 'cash',
      titleKey: 'cards.cash.title',
      receiveLabelKey: 'cards.cash.receiveLabel',
      receiveValueKey: 'cards.cash.receiveValue',
      feeLabelKey: 'cards.cash.feeLabel',
      feeValueKey: 'cards.cash.feeValue',
      icon: <BarcodeIcon size="large" />,
      plan: getPlan(PaymentMethodsType.Ticket),
    },
  ];

  return (
    <Box marginY="8" paddingY="4">
      <Box marginBottom="6">
        <Title as={isMobile ? 'h3' : 'h2'}>{t('title')}</Title>
        <Box marginTop="2">
          <Text color="neutral-textLow">{t('referencePricesLabel')}</Text>
        </Box>
      </Box>

      <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap="4">
        {planCards.map((card) => (
          <Box key={card.key} flex="1">
            <Card>
              <Card.Header>
                <Box display="flex" alignItems="center" gap="3">
                  <Box
                    borderRadius="full"
                    backgroundColor="primary-surface"
                    padding="2"
                    maxWidth="44px"
                    maxHeight="44px"
                  >
                    <Icon source={card.icon} color="primary-interactive" />
                  </Box>
                  <Title as="h4">{t(card.titleKey)}</Title>
                </Box>
              </Card.Header>

              <Card.Body>
                {isLoading ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Spinner />
                  </Box>
                ) : error || !card.plan ? (
                  <ErrorMessage />
                ) : (
                  <>
                    <Box
                      display="flex"
                      flexDirection={isMobile ? 'column' : 'row'}
                      gap="6"
                      justifyContent="space-between"
                    >
                      <Box>
                        <Text fontSize="caption" color="neutral-textLow">
                          {t(card.receiveLabelKey)}
                        </Text>
                        <Text
                          fontSize="highlight"
                          fontWeight="bold"
                          color="neutral-textHigh"
                        >
                          {t(card.receiveValueKey, {
                            count: card.plan.settlementPeriod,
                          })}
                        </Text>
                      </Box>

                      <Box>
                        <Text fontSize="caption" color="neutral-textLow">
                          {t(card.feeLabelKey)}
                        </Text>
                        <Text
                          fontSize="highlight"
                          fontWeight="bold"
                          color="success-interactive"
                        >
                          {t(card.feeValueKey, {
                            transactionFee: card.plan.transactionFee,
                            fixedFee: card.plan.fixedFee,
                          })}
                        </Text>
                      </Box>
                    </Box>

                    <Box marginTop="4">
                      <Text fontSize="caption" color="neutral-textLow">
                        {t(`cards.${card.key}.noTransactionCost`)}
                      </Text>
                    </Box>
                  </>
                )}
              </Card.Body>
            </Card>
          </Box>
        ))}
      </Box>

      <Box
        marginTop="6"
        display="flex"
        gap="4"
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent="center"
        alignItems="center"
        maxWidth={isMobile ? '250px' : 'auto'}
        marginX={isMobile ? 'auto' : 'none'}
      >
        {showPrimaryAction ? (
          <Button
            appearance="primary"
            onClick={onPrimaryAction}
            fullWidth={isMobile}
          >
            {primaryActionLabel || t('primaryActionLabel')}
          </Button>
        ) : null}

        {showSecondaryAction && onSecondaryAction ? (
          <Button
            appearance="neutral"
            onClick={onSecondaryAction}
            fullWidth={isMobile}
          >
            <Box display="flex" alignItems="center" gap="2">
              <Icon
                color="currentColor"
                source={<CalculatorIcon size="small" />}
              />
              {secondaryActionLabel || t('secondaryActionLabel')}
            </Box>
          </Button>
        ) : null}
      </Box>
    </Box>
  );
};

export default FeeCalculatorSection;
